import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowRightOutlined, PlusOutlined } from "@/config/antd";
import {
  updateStep,
  selectSubEventStep1,
  selectSubEventStep2,
  selectSubEventStep3,
  createSubEvent,
} from "@/store/redux/slices/event/sub_event_slice";
import Back from "@/components/specific/Back";
import Button from "@/components/common/Button";
import Input from "@/components/common/inputs/Input";
import MemberPicker from "@/components/specific/Member_Picker";
import TextEditor from "@/components/common/inputs/Text_Editor";
import AllocatedFundsCard from "@/pages/event_management/single_campaign/components/Allocated_Funds_Card";
import InviteMembersDialog from "@/ui/Invite_Members";
import Delete from "@/components/common/alerts/Delete";
import Success from "@/components/common/alerts/Success";
import { useLoader } from "@/hooks/LoaderProvider";
import { getOrganizationId } from "@/utils/token_functions";
import DOMPurify from "dompurify";
import CustomMoreOptions from "@/components/specific/Custom_More_Options";

const Step3AddSubEvent = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { showLoader, hideLoader } = useLoader();
  const { eventId } = useParams();
  const step1Data = useSelector(selectSubEventStep1);
  const step2Data = useSelector(selectSubEventStep2);
  const step3Data = useSelector(selectSubEventStep3);
  const organizationID = getOrganizationId();

  const [logisticsItems, setLogisticsItems] = useState(
    step3Data.logisticsItems || []
  );
  const [editingLogisticsId, setEditingLogisticsId] = useState(null);

  const [formData, setFormData] = useState({
    logistics: {
      details: "",
      type: "",
    },
    officerInCharge: [],
    budgetaryAllocation: step3Data.budgetaryAllocation || 0,
    isInviteMembersModalVisible: false,
    isDeleteModalVisible: false,
    isSuccessModalVisible: false,
    memberToDelete: null,
    showSuccessModal: false,
  });

  // Roles for officers
  const roleOptions = [
    { value: "lead", label: "Lead Officer" },
    { value: "assistant", label: "Assistant Officer" },
    { value: "coordinator", label: "Coordinator" },
  ];

  const handleMembersSelected = (newMembers) => {
    setFormData((prev) => ({
      ...prev,
      officerInCharge: [
        ...prev.officerInCharge,
        ...newMembers.filter(
          (member) =>
            !prev.officerInCharge.some((existing) => existing.id === member.id)
        ),
      ],
      isInviteMembersModalVisible: false,
    }));
  };

  const handleDeleteMember = (member) => {
    setFormData((prev) => ({
      ...prev,
      memberToDelete: member.id,
      isDeleteModalVisible: true,
    }));
  };

  const handleDeleteConfirm = () => {
    showLoader();

    setTimeout(() => {
      setFormData((prev) => ({
        ...prev,
        officerInCharge: prev.officerInCharge.filter(
          (officer) => officer.id !== prev.memberToDelete
        ),
        isDeleteModalVisible: false,
        isSuccessModalVisible: true,
        memberToDelete: null,
      }));
      hideLoader();
    }, 2000);
  };

  const handleUpdateMember = (memberId, newRole) => {
    setFormData((prev) => ({
      ...prev,
      officerInCharge: prev.officerInCharge.map((officer) =>
        officer.id === memberId ? { ...officer, role: newRole } : officer
      ),
    }));
  };

  const isLogisticsFormValid =
    formData.logistics.details.trim() !== "" &&
    formData.logistics.type.trim() !== "" &&
    formData.officerInCharge.length > 0;

  const isSubmitValid =
    (logisticsItems.length > 0 ||
      (formData.logistics.details.trim() !== "" &&
        formData.logistics.type.trim() !== "" &&
        formData.officerInCharge.length > 0)) &&
    formData.budgetaryAllocation > 0;

  const handleModifyLogistics = (logistics) => {
    setFormData((prev) => ({
      ...prev,
      logistics: {
        type: logistics.type,
        details: logistics.details,
      },
      officerInCharge: logistics.officerInCharge,
    }));

    setEditingLogisticsId(logistics.id);
  };

  const handleRemoveLogistics = (logisticsId) => {
    showLoader();

    setTimeout(() => {
      const updatedLogistics = logisticsItems.filter(
        (item) => item.id !== logisticsId
      );
      setLogisticsItems(updatedLogistics);

      dispatch(
        updateStep({
          step: "step3",
          data: {
            logisticsItems: updatedLogistics,
            budgetaryAllocation: formData.budgetaryAllocation,
          },
        })
      );

      hideLoader();
      setFormData((prev) => ({ ...prev, isSuccessModalVisible: true }));
    }, 500);
  };

  const handleSaveLogistics = () => {
    if (!isLogisticsFormValid) return;

    showLoader();

    // Sanitize the logistics details
    const purifiedLogisticsDetails = DOMPurify.sanitize(
      formData.logistics.details,
      {
        ALLOWED_TAGS: [],
        ALLOWED_ATTR: [],
      }
    ).trim();

    if (editingLogisticsId) {
      // We're editing an existing logistics item
      const updatedLogisticsItems = logisticsItems.map((item) =>
        item.id === editingLogisticsId
          ? {
              ...item,
              type: formData.logistics.type,
              details: purifiedLogisticsDetails,
              officerInCharge: formData.officerInCharge,
            }
          : item
      );

      setTimeout(() => {
        setLogisticsItems(updatedLogisticsItems);

        dispatch(
          updateStep({
            step: "step3",
            data: {
              logisticsItems: updatedLogisticsItems,
              budgetaryAllocation: formData.budgetaryAllocation,
            },
          })
        );

        setFormData((prev) => ({
          ...prev,
          logistics: {
            type: "",
            details: "",
          },
          officerInCharge: [],
          isSuccessModalVisible: true,
        }));
        setEditingLogisticsId(null);

        hideLoader();
      }, 1000);
    } else {
      // Adding a new logistics item
      const newLogisticsItem = {
        id: Date.now(),
        type: formData.logistics.type,
        details: purifiedLogisticsDetails,
        officerInCharge: formData.officerInCharge,
      };

      setTimeout(() => {
        setLogisticsItems((prev) => [...prev, newLogisticsItem]);

        dispatch(
          updateStep({
            step: "step3",
            data: {
              logisticsItems: [...logisticsItems, newLogisticsItem],
              budgetaryAllocation: formData.budgetaryAllocation,
            },
          })
        );

        setFormData((prev) => ({
          ...prev,
          logistics: {
            type: "",
            details: "",
          },
          officerInCharge: [],
          isSuccessModalVisible: true,
        }));

        hideLoader();
      }, 1000);
    }
  };

  const handleSubmit = async () => {
    showLoader();
    try {
      dispatch(
        updateStep({
          step: "step3",
          data: {
            logisticsItems,
            budgetaryAllocation: formData.budgetaryAllocation,
          },
        })
      );

      const locationData = { ...step1Data.location };
      if (locationData.url === "") {
        delete locationData.url;
      }

      // Prepare final data according to API specification
      const finalData = {
        name: step1Data.name,
        description: step1Data.description,
        duration: {
          startDate: step1Data.duration.startDate,
          startTime: step1Data.duration.startTime,
          endDate: step1Data.duration.endDate,
          endTime: step1Data.duration.endTime,
        },
        location: locationData,
        // Only pass member IDs
        members: step1Data.members.map((member) => member.id),
        manifestoID: step1Data.manifestoID,
        schedule: step2Data.schedules.map((schedule) => ({
          name: schedule.title,
          speakers: schedule.speakers.map((speaker) => speaker.id),
          duration: schedule.duration,
        })),
        logistics: logisticsItems.map((item) => ({
          type: item.type,
          details: item.details,
          // Only pass officer IDs
          officerInCharge: item.officerInCharge.map((officer) => officer.id),
        })),
        budgetaryAllocation: formData.budgetaryAllocation,
        eventID: eventId,
        organizationID: organizationID,
      };

      await dispatch(createSubEvent(finalData)).unwrap();
      hideLoader();
      setFormData((prev) => ({ ...prev, showSuccessModal: true }));
    } catch (error) {
      console.error("Failed to submit sub event:", error);
      hideLoader();
    }
  };

  const handleSuccessClose = () => {
    setFormData((prev) => ({ ...prev, showSuccessModal: false }));
    showLoader();
    setTimeout(() => {
      hideLoader();
      navigate(`/dashboard/events/${eventId}`);
    }, 2000);
  };

  const handleBack = () => {
    navigate(`/dashboard/events/${eventId}/add-sub-event/step-2`);
  };

  // Customize the button text based on whether we're editing or adding
  const saveButtonText = editingLogisticsId
    ? "Update logistics"
    : "Save and add new logistics";

  return (
    <div className="w-full my-5 text-left">
      <Back text="Back" onClick={handleBack} />

      <div className="mt-8 space-y-8">
        {/* Display existing logistics items */}
        {logisticsItems.length > 0 && (
          <div className="mb-8">
            <h3 className="mb-4 font-semibold text-bold text-darkGrey">
              Saved Logistics Items
            </h3>
            {logisticsItems.map((item) => (
              <div
                key={item.id}
                className="flex items-center justify-between p-4 border-b border-gray-200">
                <div className="flex items-center gap-4">
                  <div className="flex flex-col">
                    <span className="font-none text-primary text-small-xs">
                      TYPE
                    </span>
                    <span className="font-bold text-bold">{item.type}</span>
                  </div>
                  <div className="flex flex-col">
                    <span className="font-none text-lighterGrey text-small-xs">
                      OFFICERS
                    </span>
                    <span className="text-darkGrey text-small-xs">
                      {item.officerInCharge.length} officers assigned
                    </span>
                  </div>
                </div>
                <div className="flex items-center">
                  <Button
                    text="REMOVE"
                    className="mr-2 border border-primary text-small w-[85px] h-[34px]"
                    customBgColor="white"
                    customTextColor="primary"
                    onClick={() => handleRemoveLogistics(item.id)}
                    fontWeight="none"
                  />
                  <div onClick={(e) => e.preventDefault()}>
                    <CustomMoreOptions
                      variant="filled"
                      orientation="vertical"
                      color="black"
                      options={[
                        {
                          key: "1",
                          label: "Modify logistics",
                          onClick: () => handleModifyLogistics(item),
                        },
                      ]}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        <div>
          <h3 className="mb-2 font-semibold text-bold text-darkGrey">
            Logistics Information
          </h3>
          <div className="mb-3">
            <Input
              label="TYPE"
              placeholder="Enter logistics type"
              value={formData.logistics.type}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  logistics: { ...prev.logistics, type: e.target.value },
                }))
              }
            />
          </div>

          <div className="mb-3">
            <TextEditor
              label="DETAILS"
              placeholder="Enter logistics details"
              value={formData.logistics.details}
              onChange={(value) =>
                setFormData((prev) => ({
                  ...prev,
                  logistics: { ...prev.logistics, details: value },
                }))
              }
            />
          </div>
        </div>

        <div>
          <h3 className="mb-2 font-semibold text-bold text-darkGrey">
            Officer In Charge
          </h3>
          <p className="mb-4 text-small text-darkGrey">
            Select officers in charge for this logistics item
          </p>
          <MemberPicker
            members={formData.officerInCharge}
            roles={roleOptions}
            onOpenModal={() =>
              setFormData((prev) => ({
                ...prev,
                isInviteMembersModalVisible: true,
              }))
            }
            onDeleteMember={handleDeleteMember}
            onUpdateMember={handleUpdateMember}
          />
        </div>

        {/* Save logistics button */}
        <Button
          text={saveButtonText}
          icon={<PlusOutlined />}
          onClick={handleSaveLogistics}
          disabled={!isLogisticsFormValid}
          className="w-full border border-primary cursor-pointer text-bold h-[36px]"
          customBgColor="white"
          customTextColor="primary"
          iconPosition="left"
          fontWeight="none"
        />

        <div>
          <h3 className="mb-2 font-semibold text-bold text-darkGrey">
            Budgetary Allocation
          </h3>
          <p className="mb-4 text-small text-darkGrey">
            Manage the budget allocation for this sub-event
          </p>
          <div className="mb-3">
            <AllocatedFundsCard
              totalBudget={formData.budgetaryAllocation}
              spentPercentage={0}
            />
          </div>
          <Input
            label="SET BUDGET"
            type="number"
            placeholder="Enter budget amount"
            value={formData.budgetaryAllocation}
            onChange={(e) => {
              const value = e.target.value;
              // Only update if value is empty or a valid number
              if (value === "" || !isNaN(Number(value))) {
                setFormData((prev) => ({
                  ...prev,
                  budgetaryAllocation: value === "" ? "" : Number(value),
                }));
              }
            }}
          />
        </div>

        <Button
          text="Submit"
          icon={<ArrowRightOutlined />}
          onClick={handleSubmit}
          disabled={!isSubmitValid}
          color="primary"
          className={`
            w-full mt-8
            ${!isSubmitValid && "bg-darkBlue text-primary cursor-not-allowed"}
          `}
          customBgColor={isSubmitValid ? "primary" : "darkBlue"}
          customTextColor={isSubmitValid ? "white" : "primary"}
        />
      </div>

      <InviteMembersDialog
        isVisible={formData.isInviteMembersModalVisible}
        onClose={() =>
          setFormData((prev) => ({
            ...prev,
            isInviteMembersModalVisible: false,
          }))
        }
        onMembersSelected={handleMembersSelected}
        modalTitle="Add Officer"
        primaryHeader="CORPORATE MEMBERS"
        showSecondaryHeader={false}
        firstSectionContent="all"
      />

      <Delete
        isVisible={formData.isDeleteModalVisible}
        onClose={() =>
          setFormData((prev) => ({ ...prev, isDeleteModalVisible: false }))
        }
        onProceed={handleDeleteConfirm}
        header={`Remove ${
          formData.officerInCharge.find((o) => o.id === formData.memberToDelete)
            ?.name
        }`}
        paragraph={`Are you sure you want to remove <span class="text-primary">${
          formData.officerInCharge.find((o) => o.id === formData.memberToDelete)
            ?.name
        }</span> as an officer?`}
        proceedButtonText="Remove"
        cancelButtonText="Cancel"
      />

      <Success
        isVisible={formData.isSuccessModalVisible}
        onClose={() =>
          setFormData((prev) => ({ ...prev, isSuccessModalVisible: false }))
        }
        header={editingLogisticsId ? "Logistics Updated" : "Logistics Saved"}
        paragraph={
          editingLogisticsId
            ? "Your logistics item has been updated successfully."
            : "Your logistics item has been saved successfully."
        }
        buttonText="Close"
        onButtonClick={() =>
          setFormData((prev) => ({ ...prev, isSuccessModalVisible: false }))
        }
      />

      <Success
        isVisible={formData.showSuccessModal}
        onClose={handleSuccessClose}
        header="Sub Event Created"
        paragraph="Your sub event has been created successfully!"
        buttonText="View Event"
        onButtonClick={handleSuccessClose}
      />
    </div>
  );
};

export default Step3AddSubEvent;
